import useTitle from '@/hooks/useTitle';
import useSearchStore from '@/store/search';
import { useMemo } from 'react';
import { useState } from 'react';
import { useEffect } from 'react';
import useApiQuery from '@/hooks/useApiQuery';
import ListView from '@/components/datagrid/ListView';
import { Box, LinearProgress, Typography, Tooltip } from '@mui/material';
import ContractTimeSelector from '../contract/content/ContractTimeSelector';
import { useTheme } from '@emotion/react';

export default function ContractsSourcesPage() {
  useTitle(`Eingangs-Posten`);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [createdDate, setCreatedDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
  );
  const theme = useTheme();

  const { registerLocalSearch, unregisterLocalSearch } = useSearchStore();

  useEffect(() => {
    registerLocalSearch({
      key: 'contractSources',
      label: 'Eingangs-Posten',
      onSearch: (query) => setSearchQuery(query),
      onClear: () => setSearchQuery(''),
    });

    return () => {
      unregisterLocalSearch();
    };
  }, [registerLocalSearch, unregisterLocalSearch]);

  const mongoFilter = useMemo(() => {
    const filter = {
      $and: [{ source: 'msp' }],
    };

    if (createdDate) {
      filter.$and.push({
        createdAt: {
          $gt: createdDate,
        },
      });
    }

    if (searchQuery) {
      filter.$and.push({
        $or: [
          {
            'lastQueryResult.company': { $regex: searchQuery, $options: 'i' },
          },
          {
            'lastQueryResult.customerNumber': {
              $regex: searchQuery,
              $options: 'i',
            },
          },
        ],
      });
    }
    return filter;
  }, [createdDate, searchQuery]);

  const {
    data: datasourceArr,
    isPending,
    refetch,
  } = useApiQuery('datasource', mongoFilter, true);

  const mappedRows = useMemo(() => {
    if (!datasourceArr) return [];
    return datasourceArr.map((datasource) => {
      let customerNumber = datasource.lastQueryResult.customerNumber || '';
      let company = datasource.lastQueryResult.company || '';
      let articles = datasource.lastQueryResult.articles || [];

      return {
        _id: datasource._id,
        createdAt: new Date(datasource.createdAt).toLocaleDateString(),
        customerNumber,
        company,
        articles,
        total: datasource.lastQueryResult.total || 0,
        used: datasource.lastQueryResult.used || 0,
        percentage: datasource.lastQueryResult.percentage || 0,
      };
    });
  }, [datasourceArr]);

  const columns = [
    {
      field: 'customerNumber',
      headerName: 'Kundennummer',
      width: 150,
    },
    {
      field: 'company',
      headerName: 'Kunde',
      minWidth: 200,
      flex: 2,
    },
    {
      field: 'articles',
      headerName: 'Pos.',
      width: 80,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: (params) => params.length,
    },
    {
      field: 'percentage',
      headerName: 'Abdeckung',
      type: 'number',
      headerAlign: 'left',
      minWidth: 200,
      flex: 1,
      renderCell: ({ row }) => {
        const { total = 0, used = 0, percentage = 0 } = row || {};
        const isOverflow = used > total;
        const isUnderflow = used < total;
        const visualPercentage = Math.min(percentage, 100);
        return (
          // <Tooltip
          //   title={`Verbraucht: ${parseFloat(used.toFixed(2))} / Verfügbar: ${parseFloat(total.toFixed(2))}`}
          // >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              gap: 1,
              height: '100%',
            }}
          >
            <Box sx={{ width: '100%', mr: 1 }}>
              <LinearProgress
                variant="determinate"
                value={visualPercentage}
                color={
                  isOverflow ? 'error' : 'primary'
                  // isOverflow ? 'error' : isUnderflow ? 'warning' : 'primary'
                }
                sx={{ height: 10, borderRadius: 4 }}
              />
            </Box>
            <Box sx={{ minWidth: 35 }}>
              <Typography variant="body2" color="text.secondary">
                {`${percentage.toFixed(2)}%`}
              </Typography>
            </Box>
          </Box>
          // </Tooltip>
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Datum',
      width: 150,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: (params) => params,
    },
  ];

  const actions = [
    <ContractTimeSelector
      label="Datum"
      date={createdDate}
      onDateChange={setCreatedDate}
      theme={theme}
      size="small"
      minWidth={200}
    />,
  ];

  return (
    <>
      <ListView
        key={JSON.stringify(mongoFilter)}
        rows={mappedRows}
        columns={columns}
        actions={actions}
        loading={isPending}
        setSelectedRows={setSelectedRows}
        selectedRows={selectedRows}
        refetch={refetch}
      />
    </>
  );
}
